<script lang="ts">
import { iIcon, iSpinner } from '@inscribe/signal';
import { computed, ref } from 'vue';
import palette from '@inscribe/signal/dist/palette';

import {
  Statuses,
  statusIcon,
  statusIconColor,
  statusBackgroundColour,
  statusTextColour,
  statusColorMap,
} from '@/V2/components/status/utils';

const statusBoxBackgroundColour = (state, isHover) => {
  return statusColorMap(state, ['60', '70'], isHover);
};

const statusBoxBackgroundColourSelected = (state) => {
  return statusColorMap(state, ['90']);
};

const statusScoreTextColour = (state) => {
  return statusColorMap(state, ['110']);
};

const statusBorderColour = (state) => {
  return state === Statuses.FAILED
    ? palette['light-neutral-60']
    : 'transparent';
};

export default {
  name: 'StatusCard',
  components: {
    iIcon,
    iSpinner,
  },
  props: {
    score: {
      type: [String, Number],
      default: '',
    },
    state: {
      type: String,
      default: Statuses.LOADING,
      validator: (value: string) => Object.values(Statuses).includes(value),
    },
    label: {
      type: String,
      default: '',
    },
    hoverState: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    cssVars() {
      return {
        '--card-background-color': statusBackgroundColour(
          this.state,
          this.isHover,
        ),
        '--card-box-background-color': statusBoxBackgroundColour(
          this.state,
          this.isHover,
        ),
        '--card-box-background-color-selected':
          statusBoxBackgroundColourSelected(this.state),
        '--card-text-color': statusTextColour(this.state),
        '--card-score-text-color': statusScoreTextColour(this.state),
        '--card-border-color': statusBorderColour(this.state),
      };
    },
  },
  setup(props) {
    let isHover = ref(false);

    const setHoverState = (state) => {
      if (props.hoverState) {
        isHover.value = state;
      }
    };

    return {
      isHover,
      Statuses,
      setHoverState,
      cardIcon: computed(() => statusIcon(props.state)),
      cardIconColor: computed(() => statusIconColor(props.state)),
    };
  },
};
</script>

<template>
  <button
    class="status-card d-flex flex-column align-items-start i-radius-xl i-px-9 i-py-8 i-gap-8"
    :style="cssVars"
    data-testid="status-card"
    @mouseenter="setHoverState(true)"
    @mouseleave="setHoverState(false)"
  >
    <div
      class="first-row d-flex flex-row align-items-center justify-content-space-between i-px-2"
    >
      <div class="text d-flex flex-row align-items-center i-gap-6">
        <i-spinner v-if="state === Statuses.LOADING" size="sm"></i-spinner>
        <i-icon
          v-else
          :color="cardIconColor"
          :symbol="cardIcon"
          :size="16"
        ></i-icon>
        <div class="title i-text-button-3">
          {{ label }}
        </div>
      </div>
      <div
        v-if="typeof score !== 'undefined'"
        class="score i-text-subheading-3"
        data-testid="status-card-score"
      >
        {{ score }}
      </div>
    </div>
    <div class="progress-bars d-flex i-gap-4">
      <div
        :class="{
          selected: state === Statuses.SUCCESS,
        }"
        class="progress-bar-section one"
      ></div>
      <div
        :class="{
          selected: state === Statuses.WARNING,
        }"
        class="progress-bar-section two"
      ></div>
      <div
        :class="{
          selected: state === Statuses.ERROR,
        }"
        class="progress-bar-section three"
      ></div>
    </div>
  </button>
</template>

<style lang="scss" scoped>
.status-card {
  background-color: var(--card-background-color);
  user-select: none;
  transition: background-color 150ms ease-in-out;
  cursor: pointer;
  border: 1px solid var(--card-border-color);

  .first-row {
    width: 100%;
    height: 16px;
  }

  .title {
    color: var(--card-text-color);
  }

  .score {
    color: var(--card-score-text-color);
  }

  .i-spinner {
    padding: 0;
  }

  .progress-bars {
    width: 100%;

    .progress-bar-section {
      border-radius: 100px;
      width: 33%;
      height: 6px;
      transition: background 150ms ease-in-out;
      background: var(--card-box-background-color);

      &.selected {
        background: var(--card-box-background-color-selected);
      }
    }
  }
}
</style>
